/**
 * Authoring one scheduler: what it runs, what follows, when, and as whom.
 *
 * Two rules shape this form:
 *
 * 1. **Criteria are not re-declared here.** A step names an object and supplies
 *    VALUES for the criteria that object already declares. Re-authoring them
 *    would be a second definition free to drift from the first.
 * 2. **Nobody can read a cron string**, so the cadence always shows the next
 *    five fire times, computed on the server from the same expression it will
 *    store — never a second implementation of the same mapping in TypeScript.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  previewCadence, saveSchedule,
  type Cadence, type CadenceType, type ScheduleModel, type ScheduleStep,
  type StepKind, type TimezoneDrift,
} from '@/api/schedules'
import {
  schedulerColors as C, Chip, DriftNotice, cardStyle, inputStyle, labelStyle,
} from '@twasta/page-runtime/shell/jobs'

const KINDS: { key: StepKind; label: string; hint: string; needsObject: boolean }[] = [
  { key: 'process', label: 'General process', needsObject: true,
    hint: 'Source SQL → calculations → persist, exactly as Review & Execute runs it.' },
  { key: 'report', label: 'Report', needsObject: true,
    hint: 'A Jasper report rendered through the same service the viewer uses.' },
  { key: 'visual', label: 'Visual', needsObject: true,
    hint: 'The visual’s numbers as CSV, HTML or PDF — not a picture of the chart.' },
  { key: 'dashboard', label: 'Dashboard', needsObject: true,
    hint: 'Every tile’s dataset in one file; a failing tile reports its error.' },
  { key: 'action', label: 'Transaction action', needsObject: true,
    hint: 'An action button run over the records a criteria selection finds.' },
  { key: 'function', label: 'Function', needsObject: false,
    hint: 'A deployed business-logic function — the maintenance-job path.' },
  { key: 'playbook', label: 'Agent playbook', needsObject: true,
    hint: 'Starts an agent playbook as one of the application’s personas, on its own credit budget.' },
]

const BI_FORMATS = ['csv', 'html', 'pdf']
const REPORT_FORMATS = ['pdf', 'xlsx', 'csv', 'html', 'docx']

const CADENCES: { key: CadenceType; label: string }[] = [
  { key: 'interval', label: 'Every N' },
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'cron', label: 'Cron' },
]

const WEEKDAYS = [
  { n: 1, label: 'Mon' }, { n: 2, label: 'Tue' }, { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' }, { n: 5, label: 'Fri' }, { n: 6, label: 'Sat' },
  { n: 0, label: 'Sun' },
]

export function blankSchedule(): ScheduleModel {
  return {
    name: '',
    title: '',
    description: '',
    target: { kind: 'process', obj_name: '', args: {} },
    then: [],
    schedule: {
      type: 'daily',
      time: '02:00',
      // The author's own zone, not UTC: a person writing "02:00" means their
      // 02:00, and making them find the dropdown to say so is a trap.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    },
    run_as: 'system',
    enabled: true,
    on_failure: { stop: true, raise_work_item: true, notify_roles: [] },
  }
}

export default function ScheduleEditor({
  project, initial, objects, onSaved, onCancel,
}: {
  project: string
  initial: ScheduleModel
  /** Object names by type, for the pickers. */
  objects: Record<string, string[]>
  onSaved: (name: string) => void
  onCancel: () => void
}) {
  const [model, setModel] = useState<ScheduleModel>(() => structuredClone(initial))
  const [errors, setErrors] = useState<string[]>([])
  const [nextRuns, setNextRuns] = useState<string[]>([])
  const [cadenceText, setCadenceText] = useState('')
  const [drift, setDrift] = useState<TimezoneDrift | null>(null)
  const [saving, setSaving] = useState(false)
  const originalName = useRef(initial.name)

  const patch = useCallback((fields: Partial<ScheduleModel>) => {
    setModel((m) => ({ ...m, ...fields }))
  }, [])

  // The next-run preview comes from the server on every cadence edit — the
  // same croniter that will schedule it, so the five dates are the truth and
  // not a TypeScript approximation of it.
  useEffect(() => {
    let cancelled = false
    const id = window.setTimeout(() => {
      previewCadence(model.schedule, 5).then((res) => {
        if (cancelled) return
        if (res.success) {
          setNextRuns(res.next_runs || [])
          setCadenceText(res.cadence_text || '')
          setDrift(res.timezone_drift || null)
        } else {
          setNextRuns([])
          setCadenceText('')
        }
      }).catch(() => { if (!cancelled) setNextRuns([]) })
    }, 250)
    return () => { cancelled = true; window.clearTimeout(id) }
  }, [model.schedule])

  const steps = useMemo(
    () => [model.target, ...(model.then || [])], [model.target, model.then])

  const setStep = useCallback((index: number, step: ScheduleStep) => {
    setModel((m) => {
      if (index === 0) return { ...m, target: step }
      const then = [...(m.then || [])]
      then[index - 1] = step
      return { ...m, then }
    })
  }, [])

  const addStep = useCallback(() => {
    setModel((m) => ({
      ...m,
      then: [...(m.then || []), { kind: 'report', obj_name: '', args: {} }],
    }))
  }, [])

  const removeStep = useCallback((index: number) => {
    setModel((m) => {
      const then = [...(m.then || [])]
      then.splice(index - 1, 1)
      return { ...m, then }
    })
  }, [])

  const save = useCallback(async () => {
    setSaving(true)
    setErrors([])
    try {
      const res = await saveSchedule(project, model, originalName.current)
      toast.success('Scheduler saved')
      onSaved(res.schedule.name)
    } catch (e: unknown) {
      // 422 carries a list of things the author can fix; anything else is a
      // real failure and says so rather than being folded into field errors.
      const err = e as { response?: { status?: number; data?: { detail?: string } } }
      const detail = err?.response?.data?.detail
      if (err?.response?.status === 422 && detail) {
        setErrors(String(detail).split('; '))
      } else {
        toast.error(detail || (e instanceof Error ? e.message : 'Save failed'))
      }
    } finally {
      setSaving(false)
    }
  }, [project, model, onSaved])

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px',
      gap: 16, alignItems: 'start' }}>
      <div>
        {errors.length > 0 && (
          <div style={{
            background: C.errWeak, border: `1px solid ${C.err}33`,
            borderRadius: 8, padding: '11px 13px', marginBottom: 14,
            fontSize: '0.8825rem', color: C.err,
          }}>
            <strong>This scheduler cannot be saved yet.</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {errors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          </div>
        )}

        <Section n={1} title="Identity">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Name" hint="lowercase, underscores — the file name and the API handle">
              <input style={inputStyle} value={model.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder="nightly_stock_ageing" />
            </Field>
            <Field label="Title" hint="what people see">
              <input style={inputStyle} value={model.title || ''}
                onChange={(e) => patch({ title: e.target.value })}
                placeholder="Nightly stock ageing" />
            </Field>
          </div>
          <Field label="Description">
            <input style={inputStyle} value={model.description || ''}
              onChange={(e) => patch({ description: e.target.value })}
              placeholder="Stock ageing process, then the ageing report to Finance" />
          </Field>
        </Section>

        <Section n={2} title="What it runs">
          <StepEditor step={model.target} objects={objects} first
            onChange={(s) => setStep(0, s)} />
        </Section>

        <Section n={3} title="And then"
          aside={<Chip tone="muted">{(model.then || []).length} step
            {(model.then || []).length === 1 ? '' : 's'}</Chip>}>
          {(model.then || []).map((step, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <StepEditor step={step} objects={objects}
                onChange={(s) => setStep(i + 1, s)}
                onRemove={() => removeStep(i + 1)} />
            </div>
          ))}
          <button type="button" className="btn btn-secondary" onClick={addStep}
            style={{ fontSize: '0.8425rem', padding: '0.4rem 0.8rem' }}>
            <span className="material-icons" style={{ fontSize: 16 }}>add</span>
            Add a step
          </button>
          <div style={{ fontSize: '0.8125rem', color: C.faint, marginTop: 7 }}>
            Steps run in order and share one run record. A failure stops the chain
            and the steps behind it are recorded as skipped.
          </div>
        </Section>

        <Section n={4} title="When">
          <CadenceEditor cadence={model.schedule}
            onChange={(schedule) => patch({ schedule })} />
        </Section>

        <Section n={5} title="Who, and what if it fails">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Runs as"
              hint="system bypasses row security — right for a sweep, wrong for a regional report">
              <select style={inputStyle} value={model.run_as || 'system'}
                onChange={(e) => patch({ run_as: e.target.value })}>
                <option value="system">System</option>
                <option value="owner">The project owner</option>
              </select>
            </Field>
            <Field label="On failure">
              <select style={inputStyle}
                value={model.on_failure?.stop === false ? 'continue' : 'stop'}
                onChange={(e) => patch({
                  on_failure: { ...model.on_failure,
                    stop: e.target.value === 'stop' },
                })}>
                <option value="stop">Stop the chain</option>
                <option value="continue">Carry on with the next step</option>
              </select>
            </Field>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8,
            fontSize: '0.8825rem', color: C.ink2, marginTop: 4 }}>
            <input type="checkbox"
              checked={model.on_failure?.raise_work_item !== false}
              onChange={(e) => patch({
                on_failure: { ...model.on_failure,
                  raise_work_item: e.target.checked },
              })} />
            Raise a work item when it fails
            <span style={{ color: C.faint }}>
              — a job that fails silently is why people stop trusting automation
            </span>
          </label>
        </Section>

        <div style={{ display: 'flex', gap: 9, marginTop: 18 }}>
          <button type="button" className="btn btn-primary" onClick={() => void save()}
            disabled={saving}>
            {saving ? 'Saving…' : 'Save scheduler'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={cardStyle}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.line}`,
            fontSize: '0.875rem', fontWeight: 600 }}>
            The next five runs
          </div>
          <div style={{ padding: '11px 14px' }}>
            {cadenceText && (
              <div style={{ fontSize: '0.8825rem', color: C.ink, marginBottom: 9 }}>
                {cadenceText}
              </div>
            )}
            {nextRuns.length === 0 && (
              <div style={{ fontSize: '0.8425rem', color: C.faint }}>
                Finish the cadence to see when it will run.
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {nextRuns.map((iso, i) => (
                <Chip key={iso} tone={i === 0 ? 'accent' : 'muted'}>
                  {new Date(iso).toLocaleString(undefined, {
                    weekday: 'short', day: '2-digit', month: 'short',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </Chip>
              ))}
            </div>
          </div>
        </div>

        <DriftNotice drift={drift} />

        <div style={{
          display: 'flex', gap: 9, padding: '10px 12px', borderRadius: 8,
          fontSize: '0.8425rem', lineHeight: 1.5, background: C.infoWeak,
          border: `1px solid ${C.info}33`, color: C.info,
        }}>
          <span className="material-icons" style={{ fontSize: 17, flexShrink: 0 }}>
            info
          </span>
          <div>
            A downloaded runtime has no IOFlow. It reads this same file and
            accepts the same call from any cron you point at it — which is why
            no logic lives in the flow.
          </div>
        </div>
      </div>
    </div>
  )
}

// ── pieces ─────────────────────────────────────────────────────────────────

function Section({ n, title, aside, children }: {
  n: number; title: string; aside?: React.ReactNode; children: React.ReactNode
}) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{
          width: 20, height: 20, borderRadius: 999, background: C.accentWeak,
          color: C.accent, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '0.8125rem', fontWeight: 700,
        }}>{n}</div>
        <div style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{title}</div>
        {aside}
      </div>
      {children}
    </div>
  )
}

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500,
        color: C.ink2, marginBottom: '0.3rem' }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: '0.8125rem', color: C.faint, marginTop: 4 }}>
        {hint}</div>}
    </div>
  )
}

function StepEditor({ step, objects, first, onChange, onRemove }: {
  step: ScheduleStep
  objects: Record<string, string[]>
  first?: boolean
  onChange: (s: ScheduleStep) => void
  onRemove?: () => void
}) {
  const meta = KINDS.find((k) => k.key === step.kind)
  const objType = OBJ_TYPE_FOR[step.kind]
  const choices = objType ? objects[objType] || [] : []
  const isBi = step.kind === 'visual' || step.kind === 'dashboard'
  const formats = isBi ? BI_FORMATS : REPORT_FORMATS

  return (
    <div style={{ ...cardStyle, padding: '11px 13px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
        {KINDS.map((k) => (
          <button key={k.key} type="button"
            onClick={() => onChange({ ...step, kind: k.key, obj_name: '' })}
            style={{
              padding: '4px 11px', borderRadius: 999, cursor: 'pointer',
              fontSize: '0.8125rem', fontWeight: 600,
              border: `1px solid ${step.kind === k.key ? C.accent : C.line}`,
              background: step.kind === k.key ? C.accentWeak : 'transparent',
              color: step.kind === k.key ? C.accent : C.ink2,
            }}>
            {k.label}
          </button>
        ))}
        {onRemove && (
          <button type="button" onClick={onRemove} title="Remove this step"
            style={{ marginLeft: 'auto', background: 'none', border: 'none',
              cursor: 'pointer', color: C.ink2, display: 'flex' }}>
            <span className="material-icons" style={{ fontSize: 18 }}>close</span>
          </button>
        )}
      </div>

      {meta && <div style={{ fontSize: '0.8125rem', color: C.faint, marginBottom: 9 }}>
        {meta.hint}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 10 }}>
        {meta?.needsObject ? (
          <Field label="Object">
            <select style={inputStyle} value={step.obj_name || ''}
              onChange={(e) => onChange({ ...step, obj_name: e.target.value })}>
              <option value="">Choose…</option>
              {choices.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
        ) : (
          <Field label="Function" hint="a deployed BL function, never an expression">
            <input style={inputStyle} value={step.function || ''}
              onChange={(e) => onChange({ ...step, function: e.target.value })}
              placeholder="purge_expired" />
          </Field>
        )}
        {(step.kind === 'report' || isBi) && (
          <Field label="Format">
            <select style={inputStyle} value={step.format || (isBi ? 'csv' : 'pdf')}
              onChange={(e) => onChange({ ...step, format: e.target.value })}>
              {formats.map((f) => <option key={f} value={f}>{f.toUpperCase()}</option>)}
            </select>
          </Field>
        )}
        {step.kind === 'playbook' && (
          <Field label="Persona" hint="the persona that runs it (created at runtime)">
            <input style={inputStyle} value={step.persona || ''}
              onChange={(e) => onChange({ ...step, persona: e.target.value })}
              placeholder="Collections Agent" />
          </Field>
        )}
        {step.kind === 'action' && (
          <Field label="Action">
            <input style={inputStyle} value={step.action_name || ''}
              onChange={(e) => onChange({ ...step, action_name: e.target.value })}
              placeholder="remind" />
          </Field>
        )}
        {step.kind === 'process' && (
          <Field label="After it runs">
            <select style={inputStyle} value={step.persist === false ? 'no' : 'yes'}
              onChange={(e) => onChange({ ...step, persist: e.target.value === 'yes' })}>
              <option value="yes">Persist the result</option>
              <option value="no">Calculate only</option>
            </select>
          </Field>
        )}
      </div>

      {isBi && (
        <div style={{ fontSize: '0.8125rem', color: C.faint, marginTop: -4,
          marginBottom: 8 }}>
          {step.format === 'pdf' || !step.format
            ? <>The PDF draws the chart server-side with the table beneath it. It is
                the same data and the same kind of chart, not a pixel copy of the
                on-screen visual — a few types (map, Sankey, radar) fall back to the
                table and say so.</>
            : <>CSV and HTML carry the numbers. Choose PDF to get the chart drawn.</>}
        </div>
      )}

      <ArgsEditor args={step.args || {}}
        onChange={(args) => onChange({ ...step, args })} />

      {!first && <DeliverEditor deliver={step.deliver || {}}
        onChange={(deliver) => onChange({ ...step, deliver })} />}
    </div>
  )
}

const OBJ_TYPE_FOR: Record<string, string> = {
  process: 'G', report: 'R', visual: 'V', dashboard: 'D', action: 'T', playbook: 'Y',
}

function ArgsEditor({ args, onChange }: {
  args: Record<string, unknown>
  onChange: (a: Record<string, unknown>) => void
}) {
  const rows = Object.entries(args)
  return (
    <div style={{ border: `1px solid ${C.line}`, borderRadius: 8,
      padding: '9px 11px', background: C.s2, marginTop: 4 }}>
      <div style={{ ...labelStyle, marginBottom: 7 }}>
        Criteria values — the object declares the criteria, this supplies values
      </div>
      {rows.map(([key, value], i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px',
          gap: 7, marginBottom: 6 }}>
          <input style={{ ...inputStyle, height: 30, fontSize: '0.8625rem' }}
            value={key} placeholder="criterion"
            onChange={(e) => {
              const next: Record<string, unknown> = {}
              rows.forEach(([k, v], j) => { next[j === i ? e.target.value : k] = v })
              onChange(next)
            }} />
          <input style={{ ...inputStyle, height: 30, fontSize: '0.8625rem' }}
            value={String(value ?? '')} placeholder="@today"
            onChange={(e) => onChange({ ...args, [key]: e.target.value })} />
          <button type="button" style={{ background: 'none', border: 'none',
            cursor: 'pointer', color: C.ink2 }}
            onClick={() => {
              const next = { ...args }
              delete next[key]
              onChange(next)
            }}>
            <span className="material-icons" style={{ fontSize: 17 }}>close</span>
          </button>
        </div>
      ))}
      <button type="button" onClick={() => onChange({ ...args, '': '' })}
        style={{ background: 'none', border: 'none', cursor: 'pointer',
          color: C.accent, fontSize: '0.8225rem', fontWeight: 600, padding: 0 }}>
        + add a value
      </button>
      <div style={{ fontSize: '0.8125rem', color: C.faint, marginTop: 7 }}>
        Date tokens: <code>@today @yesterday @1stDayOfMonth @lastDayOfMonth
        @1stDayOfFinYear @last3month</code> — resolved once per run, so every
        step of a chain that starts at 23:59 sees the same day.
      </div>
    </div>
  )
}

function DeliverEditor({ deliver, onChange }: {
  deliver: NonNullable<ScheduleStep['deliver']>
  onChange: (d: NonNullable<ScheduleStep['deliver']>) => void
}) {
  const email = deliver.email || {}
  const setEmail = (patch: Partial<NonNullable<ScheduleStep['deliver']>['email']>) =>
    onChange({ ...deliver, email: { ...email, ...patch } })
  const sending = !!(email.to?.length || email.roles?.length || email.users?.length)

  return (
    <div style={{ marginTop: 9 }}>
      <div style={{ ...labelStyle, marginBottom: 7 }}>
        Where the file goes — a scheduled artefact has to land somewhere
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 9 }}>
        <input style={{ ...inputStyle, height: 30, fontSize: '0.8625rem' }}
          value={deliver.dms_repo || ''} placeholder="Document repository"
          onChange={(e) => onChange({ ...deliver, dms_repo: e.target.value })} />
        <input style={{ ...inputStyle, height: 30, fontSize: '0.8625rem' }}
          value={deliver.dms_folder || ''} placeholder="Finance/Ageing"
          onChange={(e) => onChange({ ...deliver, dms_folder: e.target.value })} />
        <input style={{ ...inputStyle, height: 30, fontSize: '0.8625rem' }}
          value={deliver.filename || ''} placeholder="Stock ageing {as_of:dd MMM}.pdf"
          onChange={(e) => onChange({ ...deliver, filename: e.target.value })} />
      </div>

      <div style={{ ...labelStyle, margin: '11px 0 7px' }}>
        And who it is sent to
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
        <input style={{ ...inputStyle, height: 30, fontSize: '0.8625rem' }}
          value={(email.roles || []).join(', ')} placeholder="Roles: finance, directors"
          onChange={(e) => setEmail({ roles: splitList(e.target.value) })} />
        <input style={{ ...inputStyle, height: 30, fontSize: '0.8625rem' }}
          value={(email.to || []).join(', ')} placeholder="Addresses: auditor@firm.com"
          onChange={(e) => setEmail({ to: splitList(e.target.value) })} />
      </div>
      {sending && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 9,
          marginTop: 9 }}>
          <input style={{ ...inputStyle, height: 30, fontSize: '0.8625rem' }}
            value={email.subject || ''}
            placeholder="Subject — default: {title} — {date}"
            onChange={(e) => setEmail({ subject: e.target.value })} />
          <textarea style={{ ...inputStyle, height: 54, padding: '7px 10px',
            fontSize: '0.8625rem', resize: 'vertical', fontFamily: 'inherit' }}
            value={email.body || ''}
            placeholder="Message — default says what ran, when, and that the file is attached"
            onChange={(e) => setEmail({ body: e.target.value })} />
        </div>
      )}
      <div style={{ fontSize: '0.8125rem', color: C.faint, marginTop: 5 }}>
        Roles resolve when the job runs, so somebody joining the role starts
        receiving it without anybody editing this. Tokens in the subject and
        message: <code>{'{title} {object} {date} {when} {filename}'}</code>.
        Leave everything blank and the file is still downloadable from the run.
      </div>
    </div>
  )
}

/** "finance, directors" -> ['finance','directors'] — and '' -> [] so an
 *  emptied box genuinely stops the sending rather than sending to ''. */
function splitList(raw: string): string[] {
  return raw.split(',').map((s) => s.trim()).filter(Boolean)
}

function CadenceEditor({ cadence, onChange }: {
  cadence: Cadence; onChange: (c: Cadence) => void
}) {
  const set = (fields: Partial<Cadence>) => onChange({ ...cadence, ...fields })
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
        {CADENCES.map((c) => (
          <button key={c.key} type="button" onClick={() => set({ type: c.key })}
            style={{
              padding: '4px 12px', borderRadius: 999, cursor: 'pointer',
              fontSize: '0.8225rem', fontWeight: 600,
              border: `1px solid ${cadence.type === c.key ? C.accent : C.line}`,
              background: cadence.type === c.key ? C.accentWeak : 'transparent',
              color: cadence.type === c.key ? C.accent : C.ink2,
            }}>
            {c.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid',
        gridTemplateColumns: cadence.type === 'interval' ? '110px 150px 1fr' : '120px 1fr',
        gap: 10 }}>
        {cadence.type === 'interval' ? (
          <>
            <Field label="Every">
              <input type="number" min={1} style={inputStyle}
                value={cadence.interval_value ?? 1}
                onChange={(e) => set({ interval_value: Number(e.target.value) })} />
            </Field>
            <Field label="Unit">
              <select style={inputStyle} value={cadence.interval_unit || 'hours'}
                onChange={(e) => set({
                  interval_unit: e.target.value as Cadence['interval_unit'],
                })}>
                <option value="minutes">minutes</option>
                <option value="hours">hours</option>
                <option value="days">days</option>
              </select>
            </Field>
          </>
        ) : cadence.type === 'cron' ? (
          <Field label="Expression">
            <input style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }}
              value={cadence.cron_expression || ''} placeholder="0 2 * * *"
              onChange={(e) => set({ cron_expression: e.target.value })} />
          </Field>
        ) : (
          <Field label="At">
            <input type="time" style={inputStyle} value={cadence.time || '00:00'}
              onChange={(e) => set({ time: e.target.value })} />
          </Field>
        )}
        <Field label="Timezone">
          <select style={inputStyle} value={cadence.timezone || 'UTC'}
            onChange={(e) => set({ timezone: e.target.value })}>
            {timezoneChoices(cadence.timezone).map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </Field>
      </div>

      {cadence.type === 'weekly' && (
        <Field label="On">
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {WEEKDAYS.map((d) => {
              const on = (cadence.days || []).includes(d.n)
              return (
                <button key={d.n} type="button"
                  onClick={() => set({
                    days: on ? (cadence.days || []).filter((x) => x !== d.n)
                      : [...(cadence.days || []), d.n],
                  })}
                  style={{
                    padding: '4px 11px', borderRadius: 999, cursor: 'pointer',
                    fontSize: '0.8225rem', fontWeight: 600,
                    border: `1px solid ${on ? C.accent : C.line}`,
                    background: on ? C.accentWeak : 'transparent',
                    color: on ? C.accent : C.ink2,
                  }}>{d.label}</button>
              )
            })}
          </div>
        </Field>
      )}

      {cadence.type === 'monthly' && (
        <Field label="Day of the month">
          <select style={{ ...inputStyle, maxWidth: 200 }}
            value={String(cadence.day ?? 1)}
            onChange={(e) => set({
              day: e.target.value === 'last' ? 'last' : Number(e.target.value),
            })}>
            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
            <option value="last">the last day</option>
          </select>
        </Field>
      )}
    </div>
  )
}

/**
 * A short list rather than all 400-odd zones: the browser's own zone, UTC, and
 * the ones a business actually schedules in. A zone we do not list can still be
 * stored — it just is not worth a scroll.
 */
function timezoneChoices(current?: string): string[] {
  const here = Intl.DateTimeFormat().resolvedOptions().timeZone
  const common = [
    'UTC', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Tokyo',
    'Europe/London', 'Europe/Berlin', 'Europe/Paris', 'America/New_York',
    'America/Chicago', 'America/Los_Angeles', 'Australia/Sydney',
  ]
  const out = [here, ...common].filter(Boolean) as string[]
  if (current && !out.includes(current)) out.unshift(current)
  return Array.from(new Set(out))
}
